import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Badge, Label, Input } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { faEye, faRedo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { USER_TYPE } from "../../../dummyData/dummyData";
import {
  setInAppCartRowsPerPage,
  setInAppCartOrderCreatedBy,
  setInAppCartOrderDate,
  setInAppCartOrderEndDate,
  setInAppCartCartName,
  clearInAppCartCartName,
  setInAppCartAgentSourceFilter,
} from "../../../redux/inAppCartOrderSlice";
import { dateTFormat, formatFirestoreTimestamp } from "../../../utility/Utility";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import {
  ORDER_CREATED_BY_CART_PERSON,
  ORDER_CREATED_BY_CUSTOMER,
} from "../../../constants/constans";
import { useFirebaseInAppCartOrders } from "../../../hooks/useFirebaseOrders";
import "../OrderList/index.css";

interface CustomItem {
  unitPrice: number;
  displayName: string;
  menuItemId: string;
}

interface OrderItem {
  unitPrice: number;
  tinyId?: number;
  unitTotalPrice?: number;
  itemType: string;
  quantity: number;
  displayName: string;
  menuItemId: string;
  customItems?: CustomItem[];
  isExpanded?: boolean;
  portionSize?: string;
  perItemManualDiscount?: number;
}

interface Order {
  id: string;
  isDeliveryOrder?: boolean;
  restaurantId: string;
  address?: string;
  phoneNumber?: string;
  paymentMethod?: string;
  displayName?: string;
  orderStatus: string;
  paymentCompleted?: boolean;
  orderItems: OrderItem[];
  orderCreatedBy: string;
  isSplitOrder?: boolean;
  parentOrderId?: string;
  orderReference: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  deliverAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  totalPrice?: number;
  cart?: {
    cartId?: string;
    displayName?: string;
  };
  customer?: {
    displayName?: string;
    phoneNumber?: string;
  };
  organization?: {
    organizationId?: string;
    id?: string;
    displayName?: string;
    phoneNumber?: string;
  };
  orderTakenBy?: {
    displayName?: string;
    userId?: string;
  };
  orderSource?: "CallCenter" | "CustomerApp" | "CustomerWeb" | "WhatsApp";
  orderNote?: string;
  packingNote?: string;
  paymentNote?: string;
  deliveryTimeSlot?: string;
  pickupTimeSlot?: string;
  deliveryDetails?: {
    coordinates?: { latitude: number; longitude: number };
    address?: string;
    deliveryContactName?: string;
    deliveryContactPhoneNumber?: string;
    deliveryInstructions?: string;
  };
  paymentSummary?: {
    totalPrice?: number;
    netAmount?: number;
    deliveryFee?: number;
    isDeliveryFree?: boolean;
    isManualDeliveryFee?: boolean;
    discount?: number;
  };
}

type AgentOptionType = "all" | "agent" | "source";

interface AgentOption {
  value: string;
  label: string;
  type: AgentOptionType;
}

interface SelectOption {
  value: string;
  label: string;
}

interface CartOption extends SelectOption {
  cartId: string;
  displayName: string;
}

const ALL_AGENT_SOURCE_OPTION: AgentOption = {
  value: "all",
  label: "All",
  type: "all",
};

const InAppCartOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  ) as string;
  const orderCreatedBy = useSelector(
    (state: any) => state.inAppCartOrder.orderCreatedBy,
  ) as string;
  const startDate = useSelector((state: any) => state.inAppCartOrder.orderDate) as string;
  const endDate = useSelector((state: any) => state.inAppCartOrder.orderEndDate) as string;
  const rowsPerPage = useSelector((state: any) => state.inAppCartOrder.rowsPerPage) as number;
  const cartName = useSelector((state: any) => state.inAppCartOrder.cartName) as string;
  const agentSourceFilter = useSelector(
    (state: any) => state.inAppCartOrder.agentSourceFilter,
  ) as AgentOption;

  const [orderList, setOrderList] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderType, setOrderType] = useState<SelectOption>({
    value: orderCreatedBy,
    label: orderCreatedBy,
  });
  const [selectedCart, setSelectedCart] = useState<CartOption | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<AgentOption>(
    agentSourceFilter || ALL_AGENT_SOURCE_OPTION,
  );
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<SelectOption[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<SelectOption | null>(null);
  const [searchOptions, setSearchOptions] = useState<CartOption[]>([]);
  const [allSourceCartOptions, setAllSourceCartOptions] = useState<CartOption[]>([]);

  const startDateObj = startDate
    ? new Date(startDate.split("T")[0] + "T00:00:00")
    : new Date(new Date().toISOString().split("T")[0] + "T00:00:00");
  const endDateObj = endDate
    ? new Date(endDate.split("T")[0] + "T23:59:59")
    : new Date(new Date().toISOString().split("T")[0] + "T23:59:59");

  const { data: ordersData, loading: isLoading } = useFirebaseInAppCartOrders(
    restaurantId,
    startDateObj,
    endDateObj,
  );

  const applyAgentFilter = (orders: Order[]) => {
    if (!selectedAgent || selectedAgent.value === "all") {
      return orders;
    }

    return orders.filter((order: Order) => {
      if (selectedAgent.type === "agent") {
        return (
          order.orderSource === "CallCenter" &&
          order.orderTakenBy?.displayName === selectedAgent.label
        );
      }
      if (selectedAgent.type === "source") {
        return order.orderSource === selectedAgent.value;
      }
      return true;
    });
  };

  const applyFilters = (orders: Order[]) => {
    let filteredOrders = orders;

    if (orderType?.value === "Customer") {
      filteredOrders = filteredOrders.filter(
        (order: Order) => order.orderCreatedBy === "Customer",
      );
    } else if (orderType?.value === "Cart") {
      filteredOrders = filteredOrders.filter(
        (order: Order) => order.orderCreatedBy === "CartPerson",
      );
    } else if (orderType?.value === "Restaurant") {
      filteredOrders = filteredOrders.filter(
        (order: Order) => order.orderCreatedBy === "Restaurant",
      );
    }

    if (selectedCart && selectedCart.value !== "all") {
      const selectedCartId = selectedCart.cartId;
      const selectedCartName = (
        selectedCart.displayName || selectedCart.label || ""
      ).toLowerCase();

      filteredOrders = filteredOrders.filter(
        (order: Order) => {
          const orderCartId = order.cart?.cartId || "";
          if (selectedCartId) {
            return orderCartId === selectedCartId;
          }

          const orderCartName = (order.cart?.displayName || "").toLowerCase();
          return orderCartName === selectedCartName;
        },
      );
    }

    if (selectedOrganization && selectedOrganization.value !== "all") {
      filteredOrders = filteredOrders.filter(
        (order: Order) =>
          order.organization?.organizationId === selectedOrganization.value,
      );
    }

    filteredOrders = applyAgentFilter(filteredOrders);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();

      filteredOrders = filteredOrders.filter((order: Order) => {
        const searchableFields = [
          order.orderReference?.toLowerCase() || "",
          order.phoneNumber?.toLowerCase() || "",
          order.customer?.displayName?.toLowerCase() || "",
          order.organization?.displayName?.toLowerCase() || "",
          order.cart?.displayName?.toLowerCase() || "",
        ];

        return searchableFields.some((field) => field.includes(q));
      });
    }

    return filteredOrders;
  };

  useEffect(() => {
    if (allOrders.length > 0) {
      const orgMap = new Map<string, string>();
      allOrders.forEach((order: Order) => {
        if (
          order.organization?.organizationId &&
          order.organization.displayName
        ) {
          orgMap.set(
            order.organization.organizationId,
            order.organization.displayName,
          );
        }
      });

      const orgOptions = Array.from(orgMap.entries()).map(
        ([id, displayName]) => ({
          value: id,
          label: displayName,
        }),
      );
      const allOption = { value: "all", label: "All" };
      setOrganizationOptions([allOption, ...orgOptions]);
      if (!selectedOrganization) {
        setSelectedOrganization(allOption);
      }
    }
  }, [allOrders, selectedOrganization]);

  useEffect(() => {
    if (allOrders.length > 0) {
      const agents = new Set<string>();
      const sources = new Set<string>();

      allOrders.forEach((order: Order) => {
        if (
          order.orderSource === "CallCenter" &&
          order.orderTakenBy?.displayName
        ) {
          agents.add(order.orderTakenBy.displayName);
        }
        if (order.orderSource && order.orderSource !== "CallCenter") {
          sources.add(order.orderSource);
        }
      });

      const agentOpts: AgentOption[] = Array.from(agents)
        .sort()
        .map((agent) => ({
          value: agent,
          label: agent,
          type: "agent",
        }));

      const sourceOpts: AgentOption[] = Array.from(sources)
        .sort()
        .map((source) => ({
          value: source,
          label:
            source === "CustomerApp"
              ? "Customer App"
              : source === "CustomerWeb"
                ? "Customer Web"
                : source === "WhatsApp"
                  ? "WhatsApp"
                  : source,
          type: "source",
        }));

      const computedAgentOptions: AgentOption[] = [
        ALL_AGENT_SOURCE_OPTION,
        ...agentOpts,
        ...sourceOpts,
      ];
      setAgentOptions(computedAgentOptions);

      const matchedSelectedAgent = computedAgentOptions.find(
        (option) =>
          option.value === selectedAgent?.value &&
          option.type === selectedAgent?.type,
      );

      if (
        matchedSelectedAgent &&
        (matchedSelectedAgent.value !== selectedAgent?.value ||
          matchedSelectedAgent.type !== selectedAgent?.type)
      ) {
        setSelectedAgent(matchedSelectedAgent);
      }
    }
  }, [allOrders, selectedAgent]);

  useEffect(() => {
    const allOption: CartOption = {
      value: "all",
      label: "All",
      cartId: "all",
      displayName: "All",
    };

    const sourceCartMap = new Map<string, CartOption>();

    allOrders.forEach((order: Order) => {
      const sourceCart = order?.cart;
      const sourceDisplayName = sourceCart?.displayName?.trim();

      if (!sourceDisplayName) {
        return;
      }

      const sourceCartId = sourceCart?.cartId || "";
      const sourceKey = sourceCartId
        ? `id:${sourceCartId}`
        : `name:${sourceDisplayName.toLowerCase()}`;

      if (!sourceCartMap.has(sourceKey)) {
        sourceCartMap.set(sourceKey, {
          value: sourceCartId || `name:${sourceDisplayName.toLowerCase()}`,
          label: sourceDisplayName,
          cartId: sourceCartId,
          displayName: sourceDisplayName,
        });
      }
    });

    const dynamicCartOptions = Array.from(sourceCartMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );

    const sourceOptions = [allOption, ...dynamicCartOptions];
    setAllSourceCartOptions(sourceOptions);
    setSearchOptions(sourceOptions);

    if (cartName) {
      const matchingCart = sourceOptions.find(
        (cart) => cart.label.toLowerCase() === cartName.toLowerCase(),
      );
      if (matchingCart) {
        setSelectedCart(matchingCart);
      } else {
        setSelectedCart(allOption);
        dispatch(setInAppCartCartName("All"));
      }
    } else {
      setSelectedCart(allOption);
      dispatch(setInAppCartCartName("All"));
    }

    const currentSelectedSource = sourceOptions.find(
      (cart) => cart.value === selectedCart?.value,
    );
    if (currentSelectedSource) {
      setSelectedCart(currentSelectedSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrders, cartName, dispatch]);

  useEffect(() => {
    if (ordersData && ordersData.orders) {
      const sortedOrders = [...(ordersData.orders as Order[])].sort((a, b) =>
        (b.orderReference || "").localeCompare(a.orderReference || "", undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );

      setAllOrders(sortedOrders);
      setOrderList(applyFilters(sortedOrders));
      return;
    }

    setAllOrders([]);
    setOrderList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData, selectedCart, orderType, selectedAgent, selectedOrganization, searchQuery]);

  useEffect(() => {
    setOrderList(applyFilters(allOrders));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent, selectedOrganization, searchQuery]);

  const handleViewDetails = (order: Order) => {
    navigate(
      order.isDeliveryOrder === true
        ? "/orders/delivery-order/view"
        : "/orders/pickup-orders/view",
      { state: { orderId: order.id } },
    );
  };

  const handleReorder = (order: Order) => {
      navigate("/orders/create-order", {
        state: {
          reorderData: {
            isDeliveryOrder: order.isDeliveryOrder ?? false,
            orderCreatedBy: order.orderCreatedBy,
            customer: order.customer ?? null,
            cart: order.cart ?? null,
            organization: order.organization
              ? {
                  organizationId:
                    order.organization.organizationId || order.organization.id,
                  id:
                    order.organization.id || order.organization.organizationId,
                  displayName: order.organization.displayName,
                  phoneNumber: order.organization.phoneNumber,
                }
              : null,
            phoneNumber:
              order.phoneNumber ?? order.customer?.phoneNumber ?? null,
            paymentMethod: order.paymentMethod ?? null,
            orderNote: order.orderNote ?? "",
            packingNote: order.packingNote ?? "",
            paymentNote: order.paymentNote ?? "",
            deliveryDetails: order.deliveryDetails ?? null,
            deliveryTimeSlot: order.deliveryTimeSlot ?? null,
            pickupTimeSlot: order.pickupTimeSlot ?? null,
            paymentSummary: order.paymentSummary ?? null,
            isManualDeliveryFee: order.paymentSummary?.isManualDeliveryFee ?? false,
            manualDeliveryFee: order.paymentSummary?.isManualDeliveryFee
              ? (order.paymentSummary?.deliveryFee ?? 0)
              : null,
            orderItems: (order.orderItems || []).map((item: any) => ({
              id: item.menuItemId,
              menuItemId: item.menuItemId,
              tinyId: item.tinyId,
              skuId: item.skuId,
              displayName: item.displayName,
              price: item.unitPrice,
              calculatedPrice: item.unitPrice,
              quantity: item.quantity,
              portionLabel: item.portionSize || "Regular",
              itemType: item.itemType,
              customItems: item.customItems || [],
            })),
          },
        },
      });
    };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleOrderDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedStartDate = new Date(e.target.value);
    const currentEndDate = new Date(endDate.split("T")[0]);

    if (selectedStartDate > currentEndDate) {
      toast.error("Start date cannot be after end date");
      return;
    }

    const selectedDate = dateTFormat(selectedStartDate);
    dispatch(setInAppCartOrderDate(selectedDate));
  };

  const handleOrderEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedEndDate = new Date(e.target.value);
    const currentStartDate = new Date(startDate.split("T")[0]);

    if (selectedEndDate < currentStartDate) {
      toast.error("End date cannot be before start date");
      return;
    }

    const selectedDate = dateTFormat(selectedEndDate);
    dispatch(setInAppCartOrderEndDate(selectedDate));
  };

  const handleOrderCreatedByChange = (selectedOption: any) => {
    const nextOrderType: SelectOption = selectedOption
      ? { value: selectedOption.value, label: selectedOption.label }
      : { value: "All", label: "All" };

    setOrderType(nextOrderType);
    dispatch(setInAppCartOrderCreatedBy(nextOrderType.value));
  };

  const handleCartSelect = (selectedOption: any) => {
    setSelectedCart(selectedOption as CartOption | null);

    if (!selectedOption) {
      dispatch(clearInAppCartCartName());
      return;
    }

    dispatch(setInAppCartCartName(selectedOption.label));
  };

  const handleAgentChange = (selectedOption: AgentOption | null) => {
    const nextOption = selectedOption || ALL_AGENT_SOURCE_OPTION;
    setSelectedAgent(nextOption);
    dispatch(setInAppCartAgentSourceFilter(nextOption));
  };

  const handleOrganizationChange = (selectedOption: SelectOption | null) => {
    setSelectedOrganization(selectedOption || { value: "all", label: "All" });
  };

  const handleInputChange = (inputValue: string) => {
    if (!inputValue || inputValue.length === 0) {
      setSearchOptions(allSourceCartOptions);
      return;
    }

    const lowerInput = inputValue.toLowerCase();
    const filteredOptions = allSourceCartOptions.filter(
      (option) =>
        option.value === "all" || option.label.toLowerCase().includes(lowerInput),
    );

    setSearchOptions(filteredOptions);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setInAppCartRowsPerPage(newRowsPerPage));
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "ApprovedByRestaurant":
        return "warning";
      case "Preparing":
        return "primary";
      case "ReadyAtKitchen":
        return "dark";
      case "IssuedByKitchen":
        return "secondary";
      case "ArrivedAtCart":
        return "secondary";
      case "PackingComplete":
        return "primary";
      case "Completed":
        return "success";
      case "Cancelled":
        return "danger";
      case "Tentative":
        return "light";
      case "InAppCart":
        return "dark";
      default:
        return "secondary";
    }
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case "ApprovedByRestaurant":
        return "Approved By Restaurant";
      case "ReadyAtKitchen":
        return "Ready At Kitchen";
      case "IssuedByKitchen":
        return "Issued By Kitchen";
      case "ArrivedAtCart":
        return "Arrived At Cart";
      case "PackingComplete":
        return "Packing Complete";
      case "InAppCart":
        return "In App Cart";
      default:
        return status;
    }
  };

  const columns: TableColumn<Order>[] = [
    {
      name: <h6 className="fw-bold">Reorder</h6>,
      cell: (row: Order) => (
        <Button
          onClick={(e) => { e.stopPropagation(); handleReorder(row); }}
          variant="light"
        >
          <FontAwesomeIcon icon={faRedo} color="#FF9A62" />
        </Button>
      ),
      width: "90px",
      center: true,
    },
    {
      name: <h6 className="fw-bold">Reference No</h6>,
      selector: (row: Order) => row.orderReference,
      sortable: true,
      cell: (row: Order) => <strong>{row.orderReference}</strong>,
    },
    {
      name: <h6 className="fw-bold">Creator</h6>,
      selector: (row: Order) =>
        row.orderCreatedBy === ORDER_CREATED_BY_CART_PERSON
          ? "Cart Person"
          : row.orderCreatedBy === ORDER_CREATED_BY_CUSTOMER
            ? "Customer"
            : "Restaurant",
      sortable: true,
      cell: (row: Order) =>
        row.orderCreatedBy === ORDER_CREATED_BY_CART_PERSON ? (
          <>Cart Person</>
        ) : row.orderCreatedBy === ORDER_CREATED_BY_CUSTOMER ? (
          <>Customer</>
        ) : (
          <>Restaurant</>
        ),
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      selector: (row: Order) => {
        if (row.orderCreatedBy === "Customer" && row.customer?.displayName) {
          return row.customer.displayName;
        }

        if (row.orderCreatedBy === "Restaurant") {
          if (row.organization?.displayName) {
            return row.organization.displayName;
          }
          if (row.customer?.displayName) {
            return row.customer.displayName;
          }
          if (row.cart?.displayName) {
            return row.cart.displayName;
          }
        }

        if (row.cart?.displayName) {
          return row.cart.displayName;
        }

        return row.orderCreatedBy;
      },
      sortable: true,
      cell: (row: Order) => {
        let creatorName = row.orderCreatedBy;

        if (row.orderCreatedBy === "Customer" && row.customer?.displayName) {
          creatorName = row.customer.displayName;
        } else if (row.orderCreatedBy === "Restaurant") {
          if (row.organization?.displayName) {
            creatorName = row.organization.displayName;
          } else if (row.customer?.displayName) {
            creatorName = row.customer.displayName;
          } else if (row.cart?.displayName) {
            creatorName = row.cart.displayName;
          }
        } else if (row.cart?.displayName) {
          creatorName = row.cart.displayName;
        }

        return <div className="d-flex align-items-center">{creatorName}</div>;
      },
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row: Order) => formatFirestoreTimestamp(row.createdAt),
      sortable: true,
      cell: (row: Order) => <div>{formatFirestoreTimestamp(row.createdAt)}</div>,
    },
    {
      name: <h6 className="fw-bold">Phone No</h6>,
      selector: (row: Order) =>
        row.phoneNumber || row.customer?.phoneNumber || "No Phone number",
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row: Order) => (
        <div className="d-flex flex-column align-items-left">
          <Badge color={getStatusBadgeColor(row.orderStatus)} className="px-4 py-2">
            <div style={{ fontSize: 12 }}>{getStatusDisplayText(row.orderStatus)}</div>
          </Badge>
        </div>
      ),
      sortable: true,
      center: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      center: true,
      cell: (row: Order) => (
        <Button onClick={() => handleViewDetails(row)} variant="light">
          <FontAwesomeIcon icon={faEye} color="#FF9A62" />
        </Button>
      ),
    },
  ];

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />

      <div className="pickup-order-management-container">
        <div className="header">
          <div className="header-content d-flex justify-content-between align-items-center">
            <h1 className="title">In App Cart Orders</h1>
          </div>
        </div>

        <Card className="mb-0 mt-1">
          <CardBody>
            <Row className="align-items-end">
              <Col lg="4" className="mb-2 mt-2">
                <Input
                  type="text"
                  value={searchQuery}
                  placeholder="Search using Order Ref, Phone Number, or Customer Name"
                  onChange={handleSearchQueryChange}
                />
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col lg="2" className="mb-3">
                <Label>Order Created By</Label>
                <Select
                  isMulti={false}
                  name="orderType"
                  value={orderType}
                  options={USER_TYPE}
                  onChange={handleOrderCreatedByChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Organization</Label>
                <Select
                  isMulti={false}
                  name="organization"
                  value={selectedOrganization}
                  options={organizationOptions}
                  onChange={handleOrganizationChange}
                  isClearable={true}
                  placeholder="Select organization..."
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Cart Name</Label>
                <Select
                  isMulti={false}
                  name="cartName"
                  value={selectedCart}
                  isSearchable={true}
                  isClearable={true}
                  isLoading={isLoading}
                  onInputChange={handleInputChange}
                  onChange={handleCartSelect}
                  options={searchOptions}
                  placeholder={isLoading ? "Loading..." : "Select cart..."}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate.split("T")[0]}
                  max={endDate.split("T")[0]}
                  onChange={handleOrderDateChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate.split("T")[0]}
                  min={startDate.split("T")[0]}
                  onChange={handleOrderEndDateChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Agent / Source</Label>
                <Select
                  isMulti={false}
                  name="agentSource"
                  value={selectedAgent}
                  options={agentOptions}
                  onChange={handleAgentChange}
                  isClearable={true}
                  placeholder="Select agent or source..."
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            {isLoading ? (
              <TableLoadingComponent numColumns={5} />
            ) : (
              <DataTable
                title=""
                columns={columns}
                data={orderList}
                pagination
                responsive
                highlightOnHover
                paginationPerPage={rowsPerPage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onRowClicked={handleViewDetails}
                pointerOnHover
              />
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default InAppCartOrders;